import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  Grid,
  MenuItem,
  Switch,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TuneIcon from "@mui/icons-material/Tune";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";

type Props = {
  open: boolean;
  onClose: () => void;
  onGenerate?: (data: any) => void;
  data?: any;
};

const tones = [
  "Professional",
  "Conversational",
  "Thought Leader",
  "Inspiring",
];

const depths = [
  {
    label: "Short",
    words: "800–1,000 words",
  },
  {
    label: "Medium",
    words: "1,200–1,500 words",
  },
  {
    label: "Long",
    words: "1,800+ words",
  },
];

const audiences = [
  "Business Leaders",
  "Developers",
  "Founders",
  "Marketers",
];

const Chip = ({ label }: { label: string }) => (
  <Box
    sx={{
      px: 1.5,
      py: 0.7,
      borderRadius: "999px",
      background: "rgba(139,92,246,0.14)",
      border: "1px solid rgba(139,92,246,0.28)",
      color: "#C4B5FD",
      fontSize: 12,
      fontWeight: 500,
    }}
  >
    {label}
  </Box>
);

const CustomizeBlogDialog: React.FC<Props> = ({
  open,
  onClose,
  onGenerate,
  data,
}) => {
  const [title, setTitle] = useState("");
  const [tone, setTone] = useState("Professional");
  const [depth, setDepth] = useState("Medium");
  const [audience, setAudience] = useState("Business Leaders");
  const [includeData, setIncludeData] = useState(true);

  useEffect(() => {
    if (data) {
      setTitle(data?.title || "");
    }
  }, [data]);

  const handleGenerate = () => {
    onGenerate?.({
      title,
      tone,
      depth,
      audience,
      includeData,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background:
            "linear-gradient(180deg, rgba(17,24,39,0.98) 0%, rgba(8,12,20,0.98) 100%)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "24px",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box p={{ xs: 2.5, md: 4 }}>
          {/* HEADER */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <Stack direction="row" spacing={1.8} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "14px",
                  background:
                    "linear-gradient(135deg,#7C3AED,#9333EA)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 30px rgba(124,58,237,0.35)",
                }}
              >
                <TuneIcon
                  sx={{
                    color: "#fff",
                    fontSize: 24,
                  }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: 28,
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  Customize Blog
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.58)",
                    fontSize: 14,
                    mt: 0.6,
                  }}
                >
                  Fine-tune your content preferences before generation.
                </Typography>
              </Box>
            </Stack>

            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Stack>

          {/* MAIN GRID */}
          <Grid container spacing={3}>
            {/* LEFT SECTION */}
            <Grid item xs={12} md={7}>
              <Stack spacing={3}>
                {/* TITLE */}
                <Box>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      mb: 1.2,
                      fontSize: 16,
                    }}
                  >
                    1. Blog Title
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.55)",
                      fontSize: 13,
                      mb: 0.5,
                      lineHeight: 1.6,
                    }}
                  >
                    You can edit the title or keep our recommended one.
                  </Typography>

                  <TextField
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter your blog title"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(255,255,255,0.03)",
                        borderRadius: "14px",
                        color: "#fff",

                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.08)",
                        },

                        "&:hover fieldset": {
                          borderColor: "rgba(255,255,255,0.15)",
                        },

                        "&.Mui-focused fieldset": {
                          borderColor: "#8B5CF6",
                        },
                      },

                      "& input": {
                        color: "#fff",
                      },
                    }}
                  />
                </Box>

                {/* TONE */}
                <Box>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      mb: 1.5,
                      fontSize: 16,
                    }}
                  >
                    2. Tone
                  </Typography>

                  <Typography
                      sx={{
                        color: "rgba(255,255,255,0.55)",
                        fontSize: 13,
                        mb: 0.5,
                        lineHeight: 1.6,
                      }}
                    >
                      Choose the tone that best fits your message.
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1.2}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {tones.map((item) => {
                      const active = tone === item;

                      return (
                        <Button
                          key={item}
                          onClick={() => setTone(item)}
                          sx={{
                            borderRadius: "14px",
                            px: 2.2,
                            py: 1,
                            textTransform: "none",
                            fontWeight: 500,
                            border: active
                              ? "1px solid #8B5CF6"
                              : "1px solid rgba(255,255,255,0.08)",

                            background: active
                              ? "rgba(139,92,246,0.16)"
                              : "rgba(255,255,255,0.02)",

                            color: "#fff",

                            "&:hover": {
                              background: active
                                ? "rgba(139,92,246,0.18)"
                                : "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          {item}
                        </Button>
                      );
                    })}
                  </Stack>
                </Box>

                {/* DEPTH */}
                <Box>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      mb: 1.5,
                      fontSize: 16,
                    }}
                  >
                    3. Content Depth
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.55)",
                      fontSize: 13,
                      mb: 0.5,
                      lineHeight: 1.6,
                    }}
                  >
                    Select how in-depth you want this blog to be.
                  </Typography>

                  <Grid container spacing={1.5}>
                    {depths.map((item) => {
                      const active = depth === item.label;

                      return (
                        <Grid item xs={12} sm={4} key={item.label}>
                          <Box
                            onClick={() => setDepth(item.label)}
                            sx={{
                              p: 2,
                              borderRadius: "16px",
                              cursor: "pointer",

                              border: active
                                ? "1px solid #8B5CF6"
                                : "1px solid rgba(255,255,255,0.08)",

                              background: active
                                ? "rgba(139,92,246,0.12)"
                                : "rgba(255,255,255,0.02)",

                              transition: "0.2s ease",

                              "&:hover": {
                                borderColor: "#8B5CF6",
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 15,
                              }}
                            >
                              {item.label}
                            </Typography>

                            <Typography
                              sx={{
                                color: "rgba(255,255,255,0.55)",
                                fontSize: 12,
                                mt: 0.7,
                              }}
                            >
                              {item.words}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                {/* AUDIENCE */}
                <Box>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      mb: 1.2,
                      fontSize: 16,
                    }}
                  >
                    4. Target Audience
                  </Typography>

                  <Typography
                      sx={{
                        color: "rgba(255,255,255,0.55)",
                        fontSize: 13,
                        mb: 0.5,
                        lineHeight: 1.6,
                      }}
                    >
                      Who is this blog primarily for?
                  </Typography>

                  <TextField
                    select
                    fullWidth
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(255,255,255,0.03)",
                        borderRadius: "14px",
                        color: "#fff",

                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.08)",
                        },

                        "&:hover fieldset": {
                          borderColor: "rgba(255,255,255,0.15)",
                        },

                        "&.Mui-focused fieldset": {
                          borderColor: "#8B5CF6",
                        },
                      },

                      "& .MuiSelect-select": {
                        color: "#fff",
                      },
                    }}
                  >
                    {audiences.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* INCLUDE DATA */}
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: 16,
                        }}
                      >
                        5. Include Data & Examples
                      </Typography>

                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.55)",
                          fontSize: 13,
                          mt: 0.5,
                          lineHeight: 1.6,
                        }}
                      >
                        Add relevant statistics, research, and real-world examples.
                      </Typography>
                    </Box>

                    <Switch
                      checked={includeData}
                      onChange={(e) =>
                        setIncludeData(e.target.checked)
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#fff",
                        },

                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#8B5CF6",
                            opacity: 1,
                          },

                        "& .MuiSwitch-track": {
                          backgroundColor:
                            "rgba(255,255,255,0.2)",
                          opacity: 1,
                        },
                      }}
                    />
                  </Stack>

                  {/* AI RECOMMENDATION */}
                  <Box
                    sx={{
                      borderRadius: "18px",
                      p: 2.2,
                      border:
                        "1px solid rgba(139,92,246,0.35)",
                      background:
                        "linear-gradient(135deg, rgba(124,58,237,0.14), rgba(147,51,234,0.05))",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "14px",
                          background:
                            "linear-gradient(135deg,#7C3AED,#9333EA)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <AutoAwesomeIcon
                          sx={{
                            color: "#fff",
                            fontSize: 22,
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            color: "#A78BFA",
                            fontWeight: 700,
                            fontSize: 15,
                            mb: 0.5,
                          }}
                        >
                          AI Recommendation
                        </Typography>

                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.72)",
                            fontSize: 13.5,
                            lineHeight: 1.7,
                          }}
                        >
                          Based on your audience and expertise,
                          we’ll generate a high-performing,
                          SEO-friendly article optimized for
                          engagement.
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Stack>
            </Grid>

            {/* RIGHT SECTION */}
            <Grid item xs={12} md={5}>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  mb: 1.2,
                  fontSize: 16,
                }}
              >
                Preview of your Blog Idea
              </Typography>

              <Stack spacing={2}>
                {/* PREVIEW CARD */}
                <Box
                  sx={{
                    borderRadius: "22px",
                    border: "1px solid rgba(255,255,255,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(17,24,39,0.96) 0%, rgba(8,12,20,0.96) 100%)",
                    overflow: "hidden",
                  }}
                >
                  {/* IMAGE */}
                  {/* <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=1200&auto=format&fit=crop"
                    alt="blog preview"
                    sx={{
                      width: "100%",
                      height: 220,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />*/}

                  {/* CONTENT */}
                  <Box p={3}>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 28,
                        lineHeight: 1.35,
                        mb: 2,
                      }}
                    >
                      {title || "Your blog preview"}
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.62)",
                        fontSize: 14,
                        lineHeight: 1.9,
                        mb: 3,
                      }}
                    >
                      A {tone.toLowerCase()} article crafted for{" "}
                      {audience}, optimized with structured content,
                      actionable insights, and engaging storytelling.
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Chip label={tone} />
                      <Chip label={depth} />
                      <Chip label={audience} />
                    </Stack>
                  </Box>
                </Box>

                {/* WHAT TO EXPECT CARD */}
                <Box
                  sx={{
                    borderRadius: "22px",
                    border: "1px solid rgba(255,255,255,0.08)",
                    background:
                      "linear-gradient(180deg, rgba(17,24,39,0.96) 0%, rgba(8,12,20,0.96) 100%)",
                    p: 3,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 20,
                      mb: 3,
                    }}
                  >
                    What to Expect
                  </Typography>

                  <Stack spacing={2.8}>
                    {[
                      {
                        icon: (
                          <DescriptionOutlinedIcon
                            sx={{
                              color: "#A78BFA",
                              fontSize: 22,
                            }}
                          />
                        ),
                        title:
                          "Well-structured blog with clear headings",
                        desc:
                          "Includes introduction, main sections, and conclusion.",
                      },

                      {
                        icon: (
                          <SearchOutlinedIcon
                            sx={{
                              color: "#A78BFA",
                              fontSize: 22,
                            }}
                          />
                        ),
                        title: "SEO-optimized content",
                        desc:
                          "Includes keywords, meta description, and internal linking opportunities.",
                      },

                      {
                        icon: (
                          <InsightsOutlinedIcon
                            sx={{
                              color: "#A78BFA",
                              fontSize: 22,
                            }}
                          />
                        ),
                        title: "Engaging & actionable",
                        desc:
                          "Includes examples, insights, and audience-focused storytelling.",
                      },
                    ].map((item, idx) => (
                      <Stack
                        key={idx}
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "14px",
                            background:
                              "linear-gradient(135deg, rgba(124,58,237,0.22), rgba(147,51,234,0.12))",
                            border:
                              "1px solid rgba(139,92,246,0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {item.icon}
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              color: "#fff",
                              fontWeight: 600,
                              fontSize: 15,
                              mb: 0.5,
                            }}
                          >
                            {item.title}
                          </Typography>

                          <Typography
                            sx={{
                              color: "rgba(255,255,255,0.58)",
                              fontSize: 13,
                              lineHeight: 1.7,
                            }}
                          >
                            {item.desc}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          {/* FOOTER */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1.5}
            mt={4}
          >
            <Button
              onClick={onClose}
              sx={{
                color: "rgba(255,255,255,0.7)",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleGenerate}
              startIcon={<AutoAwesomeIcon />}
              sx={{
                background:
                  "linear-gradient(135deg,#7C3AED,#9333EA)",
                color: "#fff",
                textTransform: "none",
                borderRadius: "14px",
                px: 3,
                py: 1.2,
                fontWeight: 600,
                boxShadow:
                  "0 10px 30px rgba(124,58,237,0.35)",

                "&:hover": {
                  opacity: 0.92,
                  background:
                    "linear-gradient(135deg,#7C3AED,#9333EA)",
                },
              }}
            >
              Generate Blog
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CustomizeBlogDialog;